<script setup lang="ts">
import { computed } from 'vue'

export type BadgeVariant =
  | 'neutral'
  | 'primary'
  | 'success'
  | 'warning'
  | 'danger'
  | 'info'
  | 'outline'

interface Props {
  variant?: BadgeVariant
  size?: 'sm' | 'md'
  dot?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'neutral',
  size: 'sm',
  dot: false,
})

const variantClass = computed(() => {
  switch (props.variant) {
    case 'primary':
      return 'bg-brand-500/15 text-brand-600 dark:text-brand-300 ring-brand-500/30'
    case 'success':
      return 'bg-success/15 text-success ring-success/30'
    case 'warning':
      return 'bg-warning/15 text-warning ring-warning/30'
    case 'danger':
      return 'bg-danger/15 text-danger ring-danger/30'
    case 'info':
      return 'bg-info/15 text-info ring-info/30'
    case 'outline':
      return 'bg-transparent text-fg-muted ring-border'
    default:
      return 'bg-bg-elevated text-fg-muted ring-border'
  }
})

const dotColor = computed(() => {
  switch (props.variant) {
    case 'primary': return 'bg-brand-500'
    case 'success': return 'bg-success'
    case 'warning': return 'bg-warning'
    case 'danger':  return 'bg-danger'
    case 'info':    return 'bg-info'
    default:        return 'bg-fg-subtle'
  }
})
</script>

<template>
  <span
    class="inline-flex items-center gap-1 rounded-full font-medium ring-1 ring-inset"
    :class="[
      variantClass,
      size === 'sm' ? 'px-2 py-0.5 text-xs' : 'px-2.5 py-1 text-sm',
    ]"
  >
    <span v-if="dot" class="h-1.5 w-1.5 rounded-full" :class="dotColor" />
    <slot />
  </span>
</template>
